import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !licenseNumber.trim()) {
      toast.error('Please fill in both fields');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/request-recovery`, {
        email, license_number: licenseNumber
      });
      setSent(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send recovery email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E4E0] p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#2A2C2A] tracking-tight">Password Recovery</h1>
            <p className="text-sm text-[#5C5F5C] mt-1">Enter your email and license number to recover your account</p>
          </div>

          {sent ? (
            <div className="text-center space-y-4" data-testid="recovery-sent">
              <CheckCircle className="w-12 h-12 text-[#4B6651] mx-auto" />
              <h2 className="text-lg font-semibold text-[#2A2C2A]">Recovery Email Sent</h2>
              <p className="text-sm text-[#5C5F5C]">
                If an account matches that email and license number, you'll receive a password reset link shortly. Check your inbox.
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-4 gap-2 border-[#E5E4E0]" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4" /> Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#2A2C2A] text-sm font-medium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F5C]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-[#E5E4E0] bg-white h-11"
                    data-testid="recovery-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="license" className="text-[#2A2C2A] text-sm font-medium">License Number</Label>
                <Input
                  id="license"
                  type="text"
                  placeholder="Enter your veterinary license number"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  className="border-[#E5E4E0] bg-white h-11"
                  data-testid="recovery-license-input"
                />
                <p className="text-xs text-[#5C5F5C]">This must match the license number on your account.</p>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#4B6651] hover:bg-[#3E5543] text-white h-11"
                data-testid="recovery-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Recovery Email
              </Button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-sm text-[#4B6651] hover:underline" data-testid="back-to-login-link">
                  <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
